import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
// stripe
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
// components
import AppModal from "../../ui/AppModal";
import StripeCheckoutForm from "../Stripe/StripeCheckoutForm";
import AdyenCheckoutForm from "../Adyen/AdyenCheckoutForm";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { paymentActionCreators } from "../../../common/store/modules/paymentModule";

interface PaymentModalProps {
  isOpen: boolean;
  bookingResponseData: any;
  togglePaymentModal: (showPaymentModal: boolean) => void;
  redirectUrl: string;
}

export default function PaymentModal({
  isOpen,
  bookingResponseData,
  togglePaymentModal,
  redirectUrl
}: PaymentModalProps) {
  const { t } = useTranslation();
  const [stripePromise, setStripePromise] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const language = localStorage.getItem("i18nextLng");

  // states
  const bookingPaymentInfo = useSelector((state: any) => state.payment.bookingPaymentInfo);
  const bookingPaymentError = useSelector((state: any) => state.payment.bookingPaymentError);
  const paymentPlatform = useSelector((state: any) => state.payment.paymentPlatform);
  // actions
  const getBookingPaymentInfo = useActions(paymentActionCreators?.getBookingPaymentInfoAction, []);
  const clearBookingPaymentInfo = useActions(paymentActionCreators?.clearBookingPaymentInfoAction, []);

  useEffect(() => {
    if (bookingPaymentError) {
      setErrorMessage(t("error.unexpected"));
    }
  }, [bookingPaymentError]);

  useEffect(() => {
    if (bookingResponseData) {
      getBookingPaymentInfo(bookingResponseData?.booking_code);
    }
    return () => {
      clearBookingPaymentInfo();
      handleCloseModal();
    };
  }, []);

  useEffect(() => {
    if (bookingPaymentInfo?.stripe_public_key) {
      // @ts-ignore
      setStripePromise(loadStripe(bookingPaymentInfo?.stripe_public_key, { locale: language }));
    }
  }, [bookingPaymentInfo]);

  const handleOpenModal = (): void => togglePaymentModal(true);

  const handleCloseModal = (): void => togglePaymentModal(false);

  const renderStripeContent = () => {
    return (
      <Box mt={2} ml="auto" mr="auto">
        {bookingPaymentInfo?.client_secret && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: bookingPaymentInfo?.client_secret, loader: "always" }}
          >
            <StripeCheckoutForm confirmationUrl={`${window.location.origin}/${language}/${redirectUrl}`} />
          </Elements>
        )}
        {bookingPaymentError && !bookingPaymentInfo?.client_secret && <div>{errorMessage}</div>}
      </Box>
    );
  };

  const renderAdyenContent = () => <AdyenCheckoutForm confirmationUrl={`/${language}/${redirectUrl}`} />;

  return (
    <AppModal
      title={t("checkout.title")}
      modalSize="md"
      isOpen={isOpen}
      handleCloseModal={handleCloseModal}
      handleOpenModal={handleOpenModal}
    >
      {paymentPlatform === "stripe" ? (
        renderStripeContent()
      ) : paymentPlatform === "adyen" ? (
        renderAdyenContent()
      ) : bookingPaymentError ? (
        <div>{t("error.unexpected")}</div>
      ) : (
        <div />
      )}
    </AppModal>
  );
}
